import _ from "lodash";

import {
  ADD_CREATIVE,
  ADD_CREATIVES,
  ADD_DIMENSIONS,
  EDIT_CREATIVE,
  DUPLICATE_CREATIVE,
  REMOVE_CREATIVE,
  SELECT_CREATIVE,
  DESELECT_CREATIVE,
  CLEAR_SELECTED_CREATIVES,
  SELECT_CREATIVES,
  DESELECT_CREATIVES,
  SET_WEIGHT_DISTRIBUTION,
  SET_ROTATION_TYPE,
  WEIGHT_OPTIONS,
  ROTATION_TYPES,
  SET_ASSIGNED_PLACEMENTS,
  ADD_DCM_CREATIVES,
  REORDER_CREATIVES,
  SET_START_DATE,
  SET_END_DATE,
  SET_HARD_CUTOFF,
  SET_CREATIVE_API_ERROR,
  ADD_BACKUP,
  ADD_COMPANION,
  RESET_CREATIVE_STATE,
  SET_DYNAMIC_TARGETING_KEYS,
  SET_WEIGHT_DISTRIBUTION_FLAG_ONLY
} from "../../actions/app/constants";

import dayjs from "dayjs";
import dayOfYear from "dayjs/plugin/dayOfYear";
dayjs.extend(dayOfYear);

const defaultState = {
  byId: {},
  allIds: [],
  selectedId: "",
  weightDistribution: WEIGHT_OPTIONS.EVEN,
  rotationType: ROTATION_TYPES.WEIGHTED,
  assignedPlacements: [],
  startDate: dayjs(),
  endDate: dayjs().dayOfYear(366).set("hour", 23).set("minute", 59),
  hardCutoff: false,
  dynamicTargetingKeys: [],
  selectedIds: [],
  apiError: { error: new Error(), isError: false }
};

export const creatives = (state = defaultState, action) => {
  let nextState = [];
  let nextStateById = _.defaultsDeep({}, state.byId);
  let nextStateAllIds = [...state.allIds];

  switch (action.type) {
    case ADD_CREATIVES:
    case ADD_DCM_CREATIVES:
      nextState = [...action.creatives, ...Object.values(state.byId)]; //convert normalized data to array of all creatives

      if (state.weightDistribution === WEIGHT_OPTIONS.EVEN) {
        nextState = calculateEvenWeights(nextState); //evenly distribute weights across creatives
      }
      let { byId, allIds } = normalizeData(nextState);

      return {
        ...state,
        byId: { ...byId },
        allIds: [...allIds],
        assignedPlacements: [...state.assignedPlacements]
      };
    case ADD_CREATIVE:
      nextState = [...Object.values(state.byId), action.creative]; //convert normalized data to array of all creatives
      if (state.weightDistribution === WEIGHT_OPTIONS.EVEN) {
        nextState = calculateEvenWeights(nextState); //evenly distribute weights across creatives
      }

      let normalizedData = normalizeData(nextState);

      return {
        ...state,
        byId: { ...normalizedData.byId },
        allIds: [...normalizedData.allIds],
        assignedPlacements: [...state.assignedPlacements],
        formState: { ...action.taxonomy }
      };
    case ADD_BACKUP:
      let nextById = {
        ...state.byId,
        [action.backupCreative.id]: { ...action.backupCreative }
      };
      action.primaryIds.forEach((primaryId) => {
        nextById[primaryId] = {
          ...nextById[primaryId],
          backupId: action.backupCreative.id
        };
      });

      return {
        ...state,
        byId: nextById,
        allIds: [...state.allIds, action.backupCreative.id],
        assignedPlacements: [...state.assignedPlacements]
      };
    case ADD_COMPANION:
      let nextByIdCompanion = {
        ...state.byId,
        [action.companionCreative.id]: { ...action.companionCreative }
      };
      action.primaryIds.forEach((primaryId) => {
        nextByIdCompanion[primaryId] = {
          ...nextByIdCompanion[primaryId],
          companionId: action.companionCreative.id
        };
      });

      return {
        ...state,
        byId: nextByIdCompanion,
        allIds: [...state.allIds, action.companionCreative.id],
        assignedPlacements: [...state.assignedPlacements]
      };
    case DUPLICATE_CREATIVE: {
      let source = state.byId[action.id];
      if (!source) return state;

      // The copy shares the original's `file` reference on purpose: postAssets
      // keys its upload/resume bookkeeping by creative id, so each copy uploads
      // and retries independently. The shared `preview` object URL is never
      // revoked, so removing one copy can't blank another's thumbnail.
      let copy = {
        ...source,
        id: action.newId,
        taxonomy: { ...source.taxonomy }
      };
      // backupId is kept, so duplicating a paired H5 gives a copy that is
      // already paired. Pointing both at one backup creative is the model the
      // rest of the app uses: addBackupCreatives assigns a single backup to N
      // selected primaries, the file uploads once, and BartAPI bundles it into
      // each primary via insertCreativeWithBackup (a BACKUP-role creative is
      // never inserted on its own). REMOVE_CREATIVE already keeps a shared
      // backup alive until its last primary is gone.
      //
      // companionId is dropped instead: in CM360 a companion is its own
      // creative, so two primaries pointing at one would insert it twice. The
      // companion step also runs after the backup step, so nothing the
      // duplicate control can reach has one yet.
      delete copy.companionId;
      delete copy.uploadedAssets;
      delete copy.traffickedAdvertiserIds;

      // Splice the copy in right after its source rather than going through
      // ADD_CREATIVES, which prepends and would scatter copies to the top.
      let withCopy = [];
      state.allIds.forEach((id) => {
        withCopy.push(state.byId[id]);
        if (id === action.id) withCopy.push(copy);
      });

      if (state.weightDistribution === WEIGHT_OPTIONS.EVEN) {
        withCopy = calculateEvenWeights(withCopy);
      }

      let duplicated = normalizeData(withCopy);

      return {
        ...state,
        byId: { ...duplicated.byId },
        allIds: [...duplicated.allIds],
        assignedPlacements: [...state.assignedPlacements]
      };
    }
    case REMOVE_CREATIVE:
      let creativeToRemove = nextStateById[action.id];
      //handle removal of backups / association
      if (creativeToRemove.creativeRole === "BACKUP") {
        nextStateAllIds.map((id) => {
          let c = nextStateById[id];
          if (c.backupId === action.id) delete c.backupId;
          return null;
        });
      } else if (creativeToRemove.creativeRole === "COMPANION") {
        nextStateAllIds.map((id) => {
          let c = nextStateById[id];
          if (c.companionId === action.id) delete c.companionId;
          return null;
        });
      } else {
        // Removing a PRIMARY — clean up linked backup and/or companion
        if (creativeToRemove.backupId) {
          let backupAssociated = false;
          Object.values(nextStateById).map((c) => {
            if (
              c.backupId === creativeToRemove.backupId &&
              c.id !== creativeToRemove.id
            )
              backupAssociated = true;
          });

          if (!backupAssociated) {
            delete nextStateById[creativeToRemove.backupId];
            nextStateAllIds = nextStateAllIds.filter(
              (id) => id !== creativeToRemove.backupId
            );
          }
        }
        if (creativeToRemove.companionId) {
          let companionAssociated = false;
          Object.values(nextStateById).map((c) => {
            if (
              c.companionId === creativeToRemove.companionId &&
              c.id !== creativeToRemove.id
            )
              companionAssociated = true;
          });

          if (!companionAssociated) {
            delete nextStateById[creativeToRemove.companionId];
            nextStateAllIds = nextStateAllIds.filter(
              (id) => id !== creativeToRemove.companionId
            );
          }
        }
      }

      delete nextStateById[action.id]; // remove creative with id

      nextStateAllIds = nextStateAllIds.filter((id) => id !== action.id); //remove id from ids list

      if (
        state.weightDistribution === WEIGHT_OPTIONS.EVEN &&
        state.allIds.length > 1
      ) {
        //Handle weight recalculation if distribution is even
        let stateArray = calculateEvenWeights(Object.values(nextStateById));
        let normalizedData = normalizeData(stateArray);

        nextStateById = normalizedData.byId;
        nextStateAllIds = normalizedData.allIds;
      }

      return {
        ...state,
        byId: nextStateById,
        allIds: nextStateAllIds,
        assignedPlacements: [...state.assignedPlacements],
        selectedId: state.selectedId === action.id ? "" : state.selectedId
      };
    case EDIT_CREATIVE:
      if (action.creative.id) {
        return {
          ...state,
          byId: { ...state.byId, [action.creative.id]: action.creative },
          allIds: [...state.allIds]
        };
      } else {
        return { ...state };
      }
    case SELECT_CREATIVE:
      // selectedIds drives the card highlight, selectedId drives the naming
      // form. Blank ids (selectCreative("") as a "clear") and duplicates used to
      // pile up here, leaving cards highlighted with no creative in the form.
      let nextSelectedIds =
        action.id && !state.selectedIds.includes(action.id)
          ? [...state.selectedIds, action.id]
          : [...state.selectedIds];

      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements],
        selectedId: action.id,
        selectedIds: nextSelectedIds
      };
    case DESELECT_CREATIVE:
      let remainingSelectedIds = state.selectedIds.filter(
        (id) => id !== action.id
      );
      // Fall back to a card that is still selected rather than blanking
      // selectedId outright — that mismatch is what left the naming form
      // disabled next to a highlighted card.
      let nextSelectedId =
        state.selectedId === action.id
          ? remainingSelectedIds.find((id) => !!state.byId[id]) || ""
          : state.selectedId;

      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements],
        selectedId: nextSelectedId,
        selectedIds: remainingSelectedIds
      };
    case SELECT_CREATIVES: {
      // Union, preserving the existing selection order and appending new ids
      // in the order given. selectedId moves to the first id of the incoming
      // batch so the naming form loads the top card of the group the user
      // just selected.
      const incomingIds = (action.ids || []).filter(
        (id) => !!id && !!state.byId[id]
      );
      const mergedSelectedIds = [...state.selectedIds];
      for (const id of incomingIds) {
        if (!mergedSelectedIds.includes(id)) mergedSelectedIds.push(id);
      }

      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements],
        selectedId: incomingIds.length ? incomingIds[0] : state.selectedId,
        selectedIds: mergedSelectedIds
      };
    }
    case DESELECT_CREATIVES: {
      const removedIds = new Set(action.ids || []);
      const keptSelectedIds = state.selectedIds.filter(
        (id) => !removedIds.has(id)
      );
      // Same fallback as DESELECT_CREATIVE: keep the form pointed at a card
      // that is still selected rather than blanking it.
      const keptSelectedId = removedIds.has(state.selectedId)
        ? keptSelectedIds.find((id) => !!state.byId[id]) || ""
        : state.selectedId;

      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements],
        selectedId: keptSelectedId,
        selectedIds: keptSelectedIds
      };
    }
    case CLEAR_SELECTED_CREATIVES:
      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements],
        selectedId: "",
        selectedIds: []
      };
    case SET_WEIGHT_DISTRIBUTION:
      if (state.allIds.length > 0) {
        let allCreatives = Object.values({ ...state.byId });

        // creativeIds scopes the recompute to a subset (e.g. one Smart
        // Upload ad group) so it doesn't touch weights belonging to other
        // ads sharing the same creative-state slice. Order is preserved by
        // mapping over allCreatives rather than concatenating the scoped
        // subset in front.
        if (action.creativeIds) {
          let scopeSet = new Set(action.creativeIds);
          let scoped = allCreatives.filter((c) => scopeSet.has(c.id));
          let computedScoped = scoped;

          if (action.weightDistribution === WEIGHT_OPTIONS.EVEN) {
            computedScoped = calculateEvenWeights(scoped);
          } else if (action.weightDistribution === WEIGHT_OPTIONS.CUSTOM) {
            computedScoped = setZeroWeights(scoped);
          }

          let computedById = new Map(computedScoped.map((c) => [c.id, c]));
          allCreatives = allCreatives.map((c) => computedById.get(c.id) || c);
        } else {
          if (action.weightDistribution === WEIGHT_OPTIONS.EVEN) {
            allCreatives = calculateEvenWeights(allCreatives);
          }

          if (action.weightDistribution === WEIGHT_OPTIONS.CUSTOM) {
            allCreatives = setZeroWeights(allCreatives);
          }
        }

        // Only per-creative weights change here, so allIds is carried over
        // untouched. Taking normalizeData's allIds instead would rebuild it
        // from byId insertion order and silently discard any sequential
        // drag-and-drop ordering (REORDER_CREATIVES writes allIds only) —
        // reorder, then click Even/Custom, and the order would reset.
        nextStateById = normalizeData(allCreatives).byId;
        nextStateAllIds = [...state.allIds];
      }
      return {
        ...state,
        byId: nextStateById,
        allIds: nextStateAllIds,
        assignedPlacements: [...state.assignedPlacements],
        weightDistribution: action.weightDistribution
      };
    case SET_WEIGHT_DISTRIBUTION_FLAG_ONLY:
      return {
        ...state,
        weightDistribution: action.weightDistribution
      };
    case SET_ROTATION_TYPE:
      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements],
        rotationType: action.rotationType
      };
    case SET_ASSIGNED_PLACEMENTS:
      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: action.assignedPlacements
      };
    case REORDER_CREATIVES:
      // Rewrite only the allIds slots the reordered (visible) creatives
      // already occupy, so hidden entries — backups, companions, creatives
      // belonging to another Smart Upload ad group — stay exactly where they
      // are instead of being shifted by a splice against mismatched indices.
      let reorderedVisible = action.orderedVisibleIds || [];
      let visibleSet = new Set(reorderedVisible);
      let nextIds = [...state.allIds];
      let slots = [];

      for (let i = 0; i < nextIds.length; i++) {
        if (visibleSet.has(nextIds[i])) slots.push(i);
      }

      for (let i = 0; i < slots.length && i < reorderedVisible.length; i++) {
        nextIds[slots[i]] = reorderedVisible[i];
      }

      return {
        ...state,
        byId: { ...state.byId },
        allIds: nextIds,
        assignedPlacements: [...state.assignedPlacements]
      };
    case SET_START_DATE:
      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements],
        startDate: dayjs(action.startDate)
      };
    case SET_END_DATE:
      let endDate = dayjs(action.endDate);
      if (!action.timeSpecified) {
        endDate = endDate.endOf("day");
      }
      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements],
        endDate: endDate
      };
    case SET_HARD_CUTOFF:
      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements],
        hardCutoff: action.hardCutoff
      };
    case SET_CREATIVE_API_ERROR:
      return {
        ...state,
        byId: { ...state.byId },
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements],
        apiError: { isError: action.isError, error: action.error }
      };
    case ADD_DIMENSIONS:
      let creativeToAddDimensions = nextStateById[action.id];
      creativeToAddDimensions.dimensions = action.dimensions;
      creativeToAddDimensions.taxonomy.dimensions = action.dimensions
        .split(" ")
        .join("");

      let returnObj = { ...state.byId, [action.id]: creativeToAddDimensions };

      if (action.primaryIds) {
        for (let i = 0; i < action.primaryIds.length; i++) {
          let primaryCreativeToAddDimensions =
            nextStateById[action.primaryIds[i]];
          primaryCreativeToAddDimensions.dimensions = action.dimensions;
          primaryCreativeToAddDimensions.taxonomy.dimensions = action.dimensions
            .split(" ")
            .join("");
          returnObj[action.primaryIds[i]] = primaryCreativeToAddDimensions;
        }
      }

      return {
        ...state,
        byId: returnObj,
        allIds: [...state.allIds],
        assignedPlacements: [...state.assignedPlacements]
      };
    case SET_DYNAMIC_TARGETING_KEYS:
      return {
        ...state,
        dynamicTargetingKeys: action.keys
      };
    case RESET_CREATIVE_STATE:
      return _.defaultsDeep({}, defaultState);
    default:
      return state;
  }
};

const normalizeData = (dataArray) => {
  let byId = {};
  let allIds = [];

  for (let i = 0; i < dataArray.length; i++) {
    let c = dataArray[i];
    byId[c.id] = { ...c }; // insert into byId object as { "[creative-id]": [creative] }
    allIds.push(c.id); // add id to the list of ids
  }

  return { byId, allIds };
};

const calculateEvenWeights = (dataArray) => {
  if (dataArray.length === 0) return [];
  let totalWeight = 0;
  let numberOfBackups = 0;

  for (let i = 0; i < dataArray.length; i++) {
    if (dataArray[i].creativeRole === "BACKUP" || dataArray[i].creativeRole === "COMPANION") numberOfBackups++;
  }

  let stateArray = dataArray.map((c) => {
    if (c.creativeRole === "PRIMARY") {
      let weight = Math.floor(100 / (dataArray.length - numberOfBackups));
      totalWeight += weight;
      return {
        ...c,
        weight
      };
    } else {
      return { ...c, weight: 0 };
    }
  });

  let difference = Math.abs(100 - totalWeight);

  if (difference !== 0) {
    for (let i = 0; i < difference; i++) {
      let currentCreative = stateArray[i];
      if (currentCreative.creativeRole === "PRIMARY") {
        currentCreative.weight += 1;
      }
    }
  }

  return stateArray;
};

const setZeroWeights = (dataArray) => {
  let stateArray = dataArray.map((c) => {
    return { ...c, weight: 0 };
  });

  return stateArray;
};
